/**
 * SeatsPickerMixin - Shared logic for seat selection components
 *
 * This mixin provides common functionality for components that handle seat selection,
 * such as SeatsPicker and SeatsAndTicketsPicker. It manages:
 *
 * - Ticket type filtering and seat availability logic
 * - Seat chart scaling and responsive behavior
 * - User seat preferences and selection state
 * - Pinch-to-zoom animations and cookie management
 *
 * Extends: ShowingMixin
 * Used by: SeatsPicker, SeatsAndTicketsPicker
 */
import { Cookies } from 'quasar'
import ShowingMixin from 'src/modules/checkout/components/mixins/ShowingMixin'
import seatSaleRestrictionService from 'src/services/seats/SeatSaleRestrictionService'

export default {
  mixins: [ShowingMixin],
  data () {
    return {
      containerWidth: null
    }
  },
  models: {
    seatChartForShowing: {
      seatChartOptions: true,
      seatChart: true,
      variables () {
        return {
          showingId: this.showingId,
          orderId: this.order?.isRemote ? this.order.id : null,
          skip: _.isNil(this.showingId)
        }
      },
      immediate: true,
      force: true
    },
    showingsForDate: {
      movie: 'default',
      screen: 'default',
      variables () {
        return {
          date: this.currentSite.siteEffectiveDateForTime(this.showing?.time),
          movieId: this.showing?.movie?.id,
          siteIds: [this.currentSite.id],
          skip: !this.showing?.loaded
        }
      }
    }
  },
  configuration: ['seatChartAreaCategories'],
  computed: {
    ticketTypeId () {
      if (!this.ticket) {
        return false // This will show all seats
      } else if (this.ticket.ticketType.appliesToSpecificSeats) {
        return this.ticket.ticketType.id
      } else {
        return null // This will only show seats that aren't set to a specific ticket type
      }
    },
    ticketTypeIds () {
      if (this.ticketTypeId === false) return false

      if (!this.ticketTypeId || !this.seatChartForShowing) return null

      // The the smallest subset of ticket type ids all sets of seats with this id has
      // Do this because this is the unit we are selecting over
      const matchingTicketTypeIds = _(this.seatChartForShowing.seatChart)
        .flatten()
        .filter(seat => !_.isNil(seat.areaCategoryId) && seatSaleRestrictionService.isSeatSaleAllowed(seat, this.ticket?.ticketType))
        .map(seat => seat.areaCategoryId)
        .compact()
        .uniq()
        .value()

      const effectiveTicketTypeIds = _(this.seatChartAreaCategories.data).filter(areaCategory => _.includes(matchingTicketTypeIds, areaCategory.id)).map('effectiveTicketTypeIds').sortBy('length').value()

      return effectiveTicketTypeIds.length ? effectiveTicketTypeIds[0] : null
    },
    seatChart () {
      return this.showing?.screen?.seatChart || [[]]
    },
    seatPreferences () {
      return this.order?.user?.seatPreferences || { y_ratio: 0.5 }
    }
  },
  watch: {
    showingId () {
      this.selectedSeats = this.checkoutData.selectedSeatsForShowingId(this.showingId)
    },
    async 'seatChartForShowing.id' () {
      this.showing.screen.seatChart = this.seatChartForShowing
      const mostRowOrColumnCount = Math.max(this.seatChartForShowing.seatChart.length, this.seatChartForShowing.seatChart[0].length)

      if (this.$q.screen.gt.xs) {
        this.autoZoom = this.$q.screen.gt.md || !this.$isLandscapeView() ? Math.round(mostRowOrColumnCount / 25) : Math.round(mostRowOrColumnCount / 14)
      } else {
        this.autoZoom = Math.round(mostRowOrColumnCount / 12)
      }
      this.maxScale = this.autoZoom + 1
      const seatChartCookies = Cookies.get(this.$constants.cookies.seatChartCookies)
      const seatChartCookiesSiteId = seatChartCookies?.siteId
      const hasSeatChartCookies = !!seatChartCookies && seatChartCookiesSiteId === this.currentSite.id
      if (!hasSeatChartCookies) {
        if (this.seatChartForShowing.loading) return

        this.showPinchZoomAnimation = true
        setTimeout(() => {
          this.showPinchZoomAnimation = false
          Cookies.set(this.$constants.cookies.seatChartCookies, { siteId: this.currentSite.id, showPinchZoomAnimation: true, type: this.$constants.cookies.type.consumerConsent, expires: 365 })
        }, 4000)
      }
      await this.$nextTick()
    }
  },
  methods: {
    handleSeatChartInput (input) {
      this.selectedSeats = input.selectedSeats
      this.selectionComplete = input.complete
      this.selectionDescription = input.description
    },
    /**
     * Resizes the seat chart to fit the available container space
     * Calculates optimal scale and positioning for different screen sizes
     * Handles responsive behavior for mobile, tablet, and desktop views
     * Emits page-max-width event for parent components
     */
    async resizeChart () {
      await this.$nextTick()
      const seatChartContainer = this.$refs.seatChartContainer
      const seatChart = this.$refs.seatChart

      if (!seatChart || !seatChartContainer) return

      const heightWithControl = this.showFullScreen
        ? window.innerHeight - 100
        : this.$q.screen.gt.xs ? seatChartContainer.clientHeight - 100 : seatChartContainer.clientHeight - 20
      const widthWithControl = this.showFullScreen
        ? window.innerWidth - 20
        : seatChartContainer.clientWidth

      const heightRatio = heightWithControl / seatChart.$refs['seat-chart-wrapper'].clientHeight
      const widthRatio = widthWithControl / seatChart.$refs['seat-chart-wrapper'].clientWidth
      let widthToFillHeight = heightRatio * widthWithControl

      // Fill screen on small phones and scroll
      const minWidth = Math.min(window.visualViewport.width, 400)
      widthToFillHeight = Math.max(widthToFillHeight, minWidth)

      const maxWidth = this.$isLandscapeView() ? window.visualViewport.width - 800 : window.visualViewport.width
      widthToFillHeight = Math.min(widthToFillHeight, maxWidth)

      if (widthToFillHeight >= widthWithControl) {
        this.pageMaxWidth = widthToFillHeight
      } else if (widthToFillHeight < widthWithControl) {
        this.pageMaxWidth = Math.min(widthWithControl, this.$el.scrollWidth)
      } else {
        this.pageMaxWidth = Math.min(window.innerWidth, 700)
      }
      this.$emit('page-max-width', this.pageMaxWidth)

      if (heightRatio < 1 && (this.$isLandscapeView() || this.showFullScreenn)) {
        this.initialScale = heightRatio
        this.originX = widthWithControl / 2
        this.originY = 0
      } else if (widthRatio < 1 && this.showFullScreen) {
        this.initialScale = widthRatio
        this.originX = 0
        this.originY = heightWithControl / 2
      } else {
        this.initialScale = 1
        this.originX = 0
        this.originY = 0
      }

      this.currentScale = this.initialScale
      this.containerWidth = widthWithControl
      this.initialMinScale = Math.max(this.initialScale - 0.5, 0.1)
    }
  }
}
